import { cloneDeep, forEach, get, keyBy, startCase, values } from 'lodash';

import { Tab } from 'screens/MissionControl/constants/settingsItems';

const RESOURCE_MAP = {
  'v2/settings/security-center/groups/:id': 'Group Details',
  'v2/settings/security-center/users/:id': 'User Details',
  'v2/settings/practice-resources/providers/:id': 'Provider Details',
  'v2/settings/practice-resources/facilities/:id': 'Facility Details',
  'v2/settings/practice-resources/appointment-types/:id': 'Appointment Type Details',
  'v2/settings/utilities/custom-actions/appointment/:id': 'Appointment Details',
  'v2/settings/utilities/custom-actions/diagnostic/:id': 'Diagnostic Details',
  'v2/settings/utilities/custom-actions/reminder/:id': 'Reminder Details',
  'v2/settings/utilities/custom-actions/procedure/:id': 'Procedure Details',
  'v2/settings/utilities/custom-actions/recall/:id': 'Recall Details',
  'v2/settings/utilities/message-templates(/:tab)(/:id)': 'Template History',
  'v2/settings/utilities/patient-billing/items/:id': 'Billing Item Edit',
};

const NOT_SCREEN_MAP = {
  '/v2/settings/utilities/custom-actions/appointment': true,
  '/v2/settings/utilities/custom-actions/diagnostic': true,
  '/v2/settings/utilities/custom-actions/reminder': true,
  '/v2/settings/utilities/custom-actions/procedure': true,
  '/v2/settings/utilities/custom-actions/recall': true,
};

const HOME_SUBMENU = {
  'my-account': {
    key: 'my-account',
    label: 'My Account',
    path: '/v2/settings/home/my-account',
  },
  'main-account': {
    key: 'main-account',
    label: 'Main Account',
    path: '/v2/settings/home/main-account',
  },
};

interface BreadcrumbTab extends Tab {
  subMenuMap?: {};
  notAScreen?: boolean;
}

interface SettingsMenuMap {
  [key: string]: BreadcrumbTab;
}

export interface BreadcrumbStep {
  label: string;
  path: string;
  menu: Tab[];
}

const stepIsNotATab = (step: string) => !!step.match(/\d/g);
const routeStepIsAnId = (step: string) => step.includes(':id');

const getStepLabel = ({ step, i, routeArray, currentPathArray, settingsMenuMap }: any) => {
  const pathToLabel = `${currentPathArray.join('.subMenuMap.')}.label`;
  const routeStep = routeArray[i];
  if (routeStepIsAnId(routeStep) && stepIsNotATab(step)) {
    const key = `v2/settings/${routeArray.slice(0, i + 1).join('/')}`;
    return RESOURCE_MAP[key as keyof typeof RESOURCE_MAP] || '';
  }
  const label = get(settingsMenuMap, `${pathToLabel}`, false);
  return label || startCase(step);
};

const getStepDropDownMenu = ({ i, pathArray, settingsMenuMap }: any) => {
  const pathToSubMenu = `${pathArray.slice(0, i).join('.subMenuMap.')}.subMenuMap`;
  return values(get(settingsMenuMap, pathToSubMenu, {}));
};

const stepShouldNotHavePath = ({ urlToCurrentStep, toTabInSettingsMenuMap, settingsMenuMap }: any) => {
  const notAScreenFlag: boolean = get(settingsMenuMap, `${toTabInSettingsMenuMap}.notAScreen`, false);
  return notAScreenFlag || NOT_SCREEN_MAP[urlToCurrentStep as keyof typeof NOT_SCREEN_MAP];
};

const getStepPath = ({ currentPathArray, settingsMenuMap }: any) => {
  const toTabInSettingsMenuMap = `${currentPathArray.join('.subMenuMap.')}`;
  const urlToCurrentStep = `/v2/settings/${currentPathArray.join('/')}`;
  if (stepShouldNotHavePath({ urlToCurrentStep, toTabInSettingsMenuMap, settingsMenuMap })) return '';
  return get(settingsMenuMap, `${toTabInSettingsMenuMap}.path`, false) || urlToCurrentStep;
};

export const getSettingsMenuMap = (menu: Tab[]) => {
  const settingsMenuMap: SettingsMenuMap = keyBy(cloneDeep(menu), 'key');
  forEach(settingsMenuMap, (tab) => {
    if (!tab.path) tab.notAScreen = true; // eslint-disable-line no-param-reassign
    if (tab.subMenu) tab.subMenuMap = getSettingsMenuMap(tab.subMenu); // eslint-disable-line no-param-reassign
  });
  if (settingsMenuMap.home) settingsMenuMap.home.subMenuMap = HOME_SUBMENU;
  return settingsMenuMap;
};

export const getBreadcrumbSteps = (pathname: string, route: string, settingsMenuMap: SettingsMenuMap) => {
  const breadcrumbArray: BreadcrumbStep[] = [];
  const routeArray = route.split('/').splice(2);
  const pathArray = pathname.split('/').splice(3);

  pathArray.forEach((step, i) => {
    const breadcrumbStep: any = {};
    const currentPathArray = pathArray.slice(0, i + 1);

    breadcrumbStep.label = getStepLabel({ step, i, routeArray, currentPathArray, settingsMenuMap });
    if (breadcrumbStep.label === '') return;
    breadcrumbStep.path = getStepPath({ currentPathArray, settingsMenuMap });
    breadcrumbStep.menu = getStepDropDownMenu({ i, pathArray, settingsMenuMap });
    breadcrumbArray.push(breadcrumbStep);
  });

  return breadcrumbArray;
};
